import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  GraduationCap,
  Target,
  Database,
  Mail,
  Users,
  Link2,
  CheckCircle2,
  Sparkles,
  ChevronRight,
  Award,
} from 'lucide-react';
import { Button, CardCorner, TranslatedText } from '../ui';
import scholarshipApiService from '../../services/api/scholarshipApiService';
import type { Scholarship } from '../../services/api/scholarshipApiService';

const HOW_STEPS = [
  { key: 'step1', num: 1 },
  { key: 'step2', num: 2 },
  { key: 'step3', num: 3 },
  { key: 'step4', num: 4 },
];

export function SoccerCollege() {
  const [scholarships, setScholarships] = useState<Scholarship[]>([]);

  useEffect(() => {
    scholarshipApiService.getPublicScholarships().then(setScholarships).catch(() => {
      // Silently fail — section still shows static marketing content
    });
  }, []);

  return (
    <section id="soccer-college" className="relative py-24 md:py-32 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-brand-darkNavy via-brand-navy to-brand-darkNavy" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(255,215,0,0.04)_0%,transparent_60%)]" />
      <div
        className="absolute inset-0 opacity-[0.02]"
        style={{
          backgroundImage: `linear-gradient(rgba(255,215,0,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(255,215,0,0.3) 1px, transparent 1px)`,
          backgroundSize: '60px 60px',
        }}
      />

      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <motion.div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-yellow/10 border border-brand-yellow/30 mb-6"
            whileHover={{ scale: 1.02 }}
          >
            <GraduationCap className="w-4 h-4 text-brand-yellow" />
            <span className="text-sm font-bold text-brand-yellow uppercase tracking-wider">
              <TranslatedText i18nKey="soccerCollege.badge" type="fade" duration={0.4} />
            </span>
          </motion.div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6 tracking-tight">
            <TranslatedText i18nKey="soccerCollege.title" type="digital" duration={0.7} />
          </h2>
          <p className="text-xl text-brand-yellow font-semibold mb-4">
            <TranslatedText i18nKey="soccerCollege.subtitle" type="fadeWords" duration={0.4} stagger={0.03} />
          </p>
          <p className="text-dark-300 leading-relaxed mb-4">
            <TranslatedText i18nKey="soccerCollege.intro" type="fade" duration={0.5} />
          </p>
          <p className="text-dark-400 text-sm mb-2">
            <TranslatedText i18nKey="soccerCollege.noBlind" type="fade" duration={0.4} />
          </p>
          <p className="text-white/90 font-medium">
            <TranslatedText i18nKey="soccerCollege.accessOnly" type="fade" duration={0.4} />
          </p>
        </motion.div>

        {/* How it works */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="mb-20"
        >
          <h3 className="text-2xl md:text-3xl font-black text-white mb-8 text-center flex items-center justify-center gap-2">
            <Sparkles className="w-8 h-8 text-brand-yellow" />
            <TranslatedText i18nKey="soccerCollege.howTitle" type="fade" duration={0.4} />
          </h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {HOW_STEPS.map((step, i) => (
              <motion.div
                key={step.key}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="relative p-5 rounded-2xl bg-brand-navy/60 border border-brand-lightBlue/20 overflow-hidden"
              >
                <CardCorner position="top-left" />
                <div className="flex items-center gap-3 mb-3">
                  <span className="w-10 h-10 rounded-xl bg-brand-yellow/20 border border-brand-yellow/40 flex items-center justify-center text-brand-yellow font-black">
                    {step.num}
                  </span>
                  <span className="text-white font-bold">
                    <TranslatedText i18nKey={`soccerCollege.${step.key}`} type="fade" duration={0.4} />
                  </span>
                </div>
                <p className="text-dark-400 text-sm leading-relaxed">
                  <TranslatedText i18nKey={`soccerCollege.${step.key}Desc`} type="fade" duration={0.4} />
                </p>
              </motion.div>
            ))}
          </div>
          <p className="text-center text-brand-yellow/90 font-medium mt-6">
            <TranslatedText i18nKey="soccerCollege.howClosing" type="fade" duration={0.4} />
          </p>
        </motion.div>

        {/* Dynamic Scholarships from backend */}
        {scholarships.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-20"
          >
            <h3 className="text-2xl md:text-3xl font-black text-white mb-8 text-center flex items-center justify-center gap-2">
              <Award className="w-8 h-8 text-brand-yellow" />
              <TranslatedText i18nKey="soccerCollege.scholarshipsTitle" type="fade" duration={0.4} />
            </h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {scholarships.filter(s => s.is_active).map((scholarship, i) => (
                <motion.div
                  key={scholarship.id}
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="relative p-6 rounded-2xl bg-brand-navy/60 border border-brand-lightBlue/20 overflow-hidden group hover:border-brand-yellow/50 transition-all"
                >
                  <CardCorner position="top-left" />
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-white font-bold text-lg">{scholarship.name}</span>
                    <span className="px-3 py-1 rounded-full bg-brand-yellow/20 text-brand-yellow font-black text-sm">
                      {scholarship.percentage}% OFF
                    </span>
                  </div>
                  {scholarship.description && (
                    <p className="text-dark-400 text-sm leading-relaxed">{scholarship.description}</p>
                  )}
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Scoring + Database + Contact - 3 cards */}
        <div className="grid lg:grid-cols-3 gap-6 mb-20">
          {/* Scoring */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative p-6 rounded-2xl bg-brand-navy/80 border border-brand-lightBlue/30 overflow-hidden"
          >
            <CardCorner position="top-right" />
            <div className="flex items-center gap-2 mb-4">
              <Target className="w-6 h-6 text-brand-yellow" />
              <h4 className="text-lg font-bold text-white">
                <TranslatedText i18nKey="soccerCollege.scoringTitle" type="fade" duration={0.4} />
              </h4>
            </div>
            <p className="text-dark-400 text-sm mb-4">
              <TranslatedText i18nKey="soccerCollege.scoringIntro" type="fade" duration={0.4} />
            </p>
            <div className="space-y-3">
              <p className="text-brand-yellow text-sm font-semibold">
                <TranslatedText i18nKey="soccerCollege.sportVars" type="fade" duration={0.3} />
              </p>
              <ul className="space-y-1 text-dark-400 text-sm">
                {['sportVar1', 'sportVar2', 'sportVar3', 'sportVar4', 'sportVar5'].map((k) => (
                  <li key={k} className="flex items-start gap-2">
                    <span className="text-brand-yellow mt-0.5">⚽</span>
                    <TranslatedText i18nKey={`soccerCollege.${k}`} type="fade" duration={0.3} />
                  </li>
                ))}
              </ul>
              <p className="text-brand-yellow text-sm font-semibold pt-2">
                <TranslatedText i18nKey="soccerCollege.academicVars" type="fade" duration={0.3} />
              </p>
              <ul className="space-y-1 text-dark-400 text-sm">
                {['acadVar1', 'acadVar2', 'acadVar3', 'acadVar4'].map((k) => (
                  <li key={k} className="flex items-start gap-2">
                    <span className="text-brand-yellow mt-0.5">📚</span>
                    <TranslatedText i18nKey={`soccerCollege.${k}`} type="fade" duration={0.3} />
                  </li>
                ))}
              </ul>
            </div>
            <p className="text-white/80 text-sm font-medium mt-4">
              <TranslatedText i18nKey="soccerCollege.scoringResult" type="fade" duration={0.4} />
            </p>
          </motion.div>

          {/* Database */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="relative p-6 rounded-2xl bg-brand-navy/80 border border-brand-lightBlue/30 overflow-hidden"
          >
            <CardCorner position="top-right" />
            <div className="flex items-center gap-2 mb-4">
              <Database className="w-6 h-6 text-brand-yellow" />
              <h4 className="text-lg font-bold text-white">
                <TranslatedText i18nKey="soccerCollege.dbTitle" type="fade" duration={0.4} />
              </h4>
            </div>
            <p className="text-dark-400 text-sm mb-4">
              <TranslatedText i18nKey="soccerCollege.dbIntro" type="fade" duration={0.4} />
            </p>
            <ul className="space-y-2 text-dark-400 text-sm">
              {['dbItem1', 'dbItem2', 'dbItem3'].map((k) => (
                <li key={k} className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                  <TranslatedText i18nKey={`soccerCollege.${k}`} type="fade" duration={0.3} />
                </li>
              ))}
            </ul>
            <p className="text-brand-yellow/90 text-sm font-medium mt-4">
              <TranslatedText i18nKey="soccerCollege.dbClosing" type="fade" duration={0.4} />
            </p>
          </motion.div>

          {/* Contact */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="relative p-6 rounded-2xl bg-brand-navy/80 border border-brand-lightBlue/30 overflow-hidden"
          >
            <CardCorner position="top-right" />
            <div className="flex items-center gap-2 mb-4">
              <Link2 className="w-6 h-6 text-brand-yellow" />
              <h4 className="text-lg font-bold text-white">
                <TranslatedText i18nKey="soccerCollege.contactTitle" type="fade" duration={0.4} />
              </h4>
            </div>
            <p className="text-dark-400 text-sm mb-4">
              <TranslatedText i18nKey="soccerCollege.contactIntro" type="fade" duration={0.4} />
            </p>
            <ul className="space-y-2 text-dark-400 text-sm">
              {['contact1', 'contact2', 'contact3', 'contact4'].map((k) => (
                <li key={k} className="flex items-start gap-2">
                  <Mail className="w-4 h-4 text-brand-yellow flex-shrink-0 mt-0.5" />
                  <TranslatedText i18nKey={`soccerCollege.${k}`} type="fade" duration={0.3} />
                </li>
              ))}
            </ul>
            <p className="text-white/80 text-sm font-medium mt-4">
              <TranslatedText i18nKey="soccerCollege.contactClosing" type="fade" duration={0.4} />
            </p>
          </motion.div>
        </div>

        {/* What coaches receive */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-20"
        >
          <h3 className="text-2xl font-black text-white mb-6 flex items-center gap-2">
            <Users className="w-7 h-7 text-brand-yellow" />
            <TranslatedText i18nKey="soccerCollege.coachTitle" type="fade" duration={0.4} />
          </h3>
          <div className="p-6 rounded-2xl bg-brand-navy/60 border border-brand-lightBlue/20">
            <p className="text-dark-300 mb-4">
              <TranslatedText i18nKey="soccerCollege.coachIntro" type="fade" duration={0.4} />
            </p>
            <ul className="grid sm:grid-cols-2 gap-2 text-dark-400 text-sm">
              {['coach1', 'coach2', 'coach3', 'coach4'].map((k) => (
                <li key={k} className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                  <TranslatedText i18nKey={`soccerCollege.${k}`} type="fade" duration={0.3} />
                </li>
              ))}
            </ul>
            <p className="text-brand-yellow/90 font-medium mt-4">
              <TranslatedText i18nKey="soccerCollege.coachClosing" type="fade" duration={0.4} />
            </p>
          </div>
        </motion.div>

        {/* Ecosystem + Benefits - 2 columns */}
        <div className="grid lg:grid-cols-2 gap-8 mb-20">
          <motion.div
            initial={{ opacity: 0, x: -15 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="p-6 rounded-2xl bg-brand-navy/60 border border-brand-lightBlue/20"
          >
            <h4 className="text-xl font-bold text-white mb-4">
              <TranslatedText i18nKey="soccerCollege.ecosystemTitle" type="fade" duration={0.4} />
            </h4>
            <p className="text-dark-400 text-sm mb-4">
              <TranslatedText i18nKey="soccerCollege.ecosystemIntro" type="fade" duration={0.4} />
            </p>
            <ul className="space-y-2 text-dark-400 text-sm mb-4">
              {['eco1', 'eco2', 'eco3'].map((k) => (
                <li key={k} className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-brand-yellow flex-shrink-0 mt-0.5" />
                  <TranslatedText i18nKey={`soccerCollege.${k}`} type="fade" duration={0.3} />
                </li>
              ))}
            </ul>
            <p className="text-white/90 text-sm font-medium">
              <TranslatedText i18nKey="soccerCollege.ecosystemClosing" type="fade" duration={0.4} />
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 15 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="p-6 rounded-2xl bg-brand-navy/60 border border-brand-lightBlue/20"
          >
            <h4 className="text-xl font-bold text-white mb-4">
              <TranslatedText i18nKey="soccerCollege.benefitsTitle" type="fade" duration={0.4} />
            </h4>
            <ul className="space-y-2 text-dark-400 text-sm">
              {['benefit1', 'benefit2', 'benefit3', 'benefit4', 'benefit5'].map((k) => (
                <li key={k} className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                  <TranslatedText i18nKey={`soccerCollege.${k}`} type="fade" duration={0.3} />
                </li>
              ))}
            </ul>
          </motion.div>
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center p-8 md:p-10 rounded-3xl bg-gradient-to-br from-brand-yellow/10 to-amber-500/5 border-2 border-brand-yellow/30"
        >
          <h3 className="text-2xl md:text-3xl font-black text-white mb-4">
            <TranslatedText i18nKey="soccerCollege.ctaTitle" type="fade" duration={0.5} />
          </h3>
          <p className="text-dark-300 max-w-2xl mx-auto mb-6">
            <TranslatedText i18nKey="soccerCollege.ctaSubtitle" type="fade" duration={0.4} />
          </p>
          <ul className="flex flex-wrap justify-center gap-4 mb-8 text-sm text-dark-300">
            {['ctaBullet1', 'ctaBullet2', 'ctaBullet3', 'ctaBullet4'].map((k) => (
              <li key={k} className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-brand-yellow" />
                <TranslatedText i18nKey={`soccerCollege.${k}`} type="fade" duration={0.3} />
              </li>
            ))}
          </ul>
          <div className="flex flex-wrap justify-center gap-4">
            <Button variant="primary" size="lg" icon={<ChevronRight className="w-5 h-5" />} iconPosition="right">
              <TranslatedText i18nKey="soccerCollege.ctaButton" type="fade" duration={0.3} />
            </Button>
            <Button variant="outline" size="lg">
              <TranslatedText i18nKey="soccerCollege.ctaDemo" type="fade" duration={0.3} />
            </Button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
